/**
 * Detected Payments List
 * Shows payments detected during QBO sync that need manual confirmation
 */

import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { AlertCircle, CheckCircle, X } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'
import { useQBODetectedPayments, useDismissDetectedPayment, useConfirmDetectedPayment } from '../hooks/useQuickBooks'
import { PaymentForm } from '@/features/payments/components/PaymentForm'
import { Modal } from '@/components/ui/Modal'

interface DetectedPaymentsListProps {
  clientId: string
}

export function DetectedPaymentsList({ clientId }: DetectedPaymentsListProps) {
  const { data: detectedPayments, isLoading } = useQBODetectedPayments(clientId)
  const dismissMutation = useDismissDetectedPayment()
  const confirmMutation = useConfirmDetectedPayment()
  const [confirmingPayment, setConfirmingPayment] = useState<any>(null)

  if (isLoading) {
    return null
  }

  if (!detectedPayments || detectedPayments.length === 0) {
    return null
  }

  const handleConfirm = (payment: any) => {
    setConfirmingPayment(payment)
  }

  const handleDismiss = async (paymentId: string) => {
    if (!confirm('Are you sure you want to dismiss this detected payment?')) {
      return
    }

    try {
      await dismissMutation.mutateAsync({ paymentId, clientId })
    } catch (error) {
      console.error('Failed to dismiss payment:', error)
      alert('Failed to dismiss payment. Please try again.')
    }
  }

  const handlePaymentSubmit = async (data: any) => {
    try {
      // Confirm the detected payment (creates payment_received + marks as confirmed)
      await confirmMutation.mutateAsync({
        detectedPayment: confirmingPayment,
        paymentData: data,
        clientId,
      })

      setConfirmingPayment(null)
      alert('Payment confirmed and recorded successfully')
    } catch (error) {
      console.error('Failed to confirm payment:', error)
      throw error
    }
  }

  return (
    <>
      <Card>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Detected Payments ({detectedPayments.length})
            </h3>
            <Badge variant="warning">Needs Review</Badge>
          </div>

          <p className="text-sm text-gray-600">
            These payments were detected during QuickBooks sync. Review and confirm to record them in the system.
          </p>

          {/* Payment List */}
          <div className="space-y-3">
            {detectedPayments.map((payment: any) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-gray-900">
                      {payment.customer_name}
                    </p>
                    <Badge variant="default" className="text-xs">
                      Invoice #{payment.invoice_number}
                    </Badge>
                  </div>
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                    <span className="font-medium">{formatCurrency(payment.amount)}</span>
                    {payment.payment_date && (
                      <>
                        <span>•</span>
                        <span>Paid: {formatDate(payment.payment_date)}</span>
                      </>
                    )}
                    {payment.payment_method && (
                      <>
                        <span>•</span>
                        <span>{payment.payment_method}</span>
                      </>
                    )}
                    {payment.transaction_type && payment.transaction_type !== 'payment' && (
                      <>
                        <span>•</span>
                        <Badge variant="info" className="text-xs">
                          {payment.transaction_type === 'credit_memo' ? 'Credit Memo' : payment.transaction_type}
                        </Badge>
                      </>
                    )}
                  </div>
                  {payment.qbo_payment_ref_number && (
                    <p className="text-sm text-gray-500 mt-1">Ref: {payment.qbo_payment_ref_number}</p>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    size="sm"
                    onClick={() => handleConfirm(payment)}
                    className="flex items-center space-x-1"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>Confirm</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDismiss(payment.id)}
                    loading={dismissMutation.isPending}
                    className="flex items-center space-x-1"
                  >
                    <X className="h-4 w-4" />
                    <span>Dismiss</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Confirm Payment Modal */}
      {confirmingPayment && (
        <Modal
          isOpen={!!confirmingPayment}
          onClose={() => setConfirmingPayment(null)}
          title="Confirm Payment"
        >
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Detected Payment</strong>
              </p>
              <p className="text-sm text-blue-800 mt-1">
                {confirmingPayment.customer_name} - Invoice #{confirmingPayment.invoice_number}
              </p>
              <p className="text-lg font-bold text-blue-900 mt-2">
                {formatCurrency(confirmingPayment.amount)}
              </p>
            </div>

            <p className="text-sm text-gray-600">
              Review and complete the payment details below. The amount is pre-filled from QuickBooks.
            </p>

            <PaymentForm
              initialData={{
                client_id: clientId,
                amount: confirmingPayment.amount,
                payment_date: confirmingPayment.payment_date || new Date().toISOString().split('T')[0],
                payment_method: confirmingPayment.payment_method || undefined,
              }}
              onSubmit={handlePaymentSubmit}
              onCancel={() => setConfirmingPayment(null)}
              loading={confirmMutation.isPending}
            />
          </div>
        </Modal>
      )}
    </>
  )
}
